<style scoped>
.modal.show {
	background-color: rgba(0, 0, 0, 0.5);
}
</style>

<template>
	<div
		class="modal fade"
		:class="{ 'show d-block': show }"
		tabindex="-1"
		v-show="show"
		@click.self="handleCancel"
	>
		<div class="modal-dialog modal-dialog-centered">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">
						<i class="bi bi-pencil me-2"></i>Edit Site
					</h5>
					<button
						type="button"
						class="btn-close"
						@click="handleCancel"
					></button>
				</div>
				<div class="modal-body">
					<div class="mb-3">
						<label class="form-label">Address</label>
						<div class="d-flex align-items-center gap-2">
							<span class="form-control-plaintext py-0">{{
								formatAddress(address)
							}}</span>
							<button
								type="button"
								class="btn btn-outline-secondary btn-sm text-nowrap"
								@click="showAddressModal = true"
							>
								<i class="bi bi-geo-alt me-1"></i>Change address
							</button>
						</div>
					</div>

					<div class="mb-3">
						<label for="site-edit-owner" class="form-label">Owner</label>
						<input
							id="site-edit-owner"
							v-model="owner"
							type="text"
							class="form-control"
							placeholder="Owner name"
						/>
						<div class="form-text">Leave empty to clear the owner.</div>
					</div>

					<div class="mb-3">
						<label for="site-edit-resident" class="form-label">Resident</label>
						<input
							id="site-edit-resident"
							v-model="resident"
							type="text"
							class="form-control"
							placeholder="Resident name"
						/>
						<div class="form-text">Leave empty to clear the resident.</div>
					</div>

					<div class="mb-3">
						<label for="site-edit-notes" class="form-label">Notes</label>
						<textarea
							id="site-edit-notes"
							v-model="notes"
							class="form-control"
							rows="3"
							placeholder="Notes"
						></textarea>
					</div>
				</div>
				<div class="modal-footer">
					<button
						type="button"
						class="btn btn-outline-secondary"
						@click="handleCancel"
					>
						Cancel
					</button>
					<ButtonLoading
						@click="handleSave"
						:disabled="!address.gid"
						:loading="saving"
						icon="bi-check-lg"
						text="Save"
						variant="primary"
					/>
				</div>
			</div>
		</div>
	</div>
	<div
		class="modal-backdrop fade show"
		v-show="show"
		@click="handleCancel"
	></div>

	<!-- The address picker is its own modal, dialogs over this one: the Address
	     it hands back only replaces the local ref, so nothing is written until
	     Save sends the site's four fields together. -->
	<AddressEditModal
		:address="address"
		:show="showAddressModal"
		@close="showAddressModal = false"
		@save="onAddressSaved"
	/>

	<!-- This modal's own toast: a failed save leaves the form open, and the
	     view's toast would report it from underneath the dialog. -->
	<ToastNotification
		v-model:show="toastShow"
		:title="toastTitle"
		:message="toastMessage"
	/>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";

import { apiClient } from "@/client";
import AddressEditModal from "@/components/AddressEditModal.vue";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { formatAddress } from "@/format";
import { log } from "@/log";
import { Address, type Site, type SiteUpdateRequest } from "@/type/api";

interface Emits {
	(e: "close"): void;
	(e: "saved"): void;
}
interface Props {
	site: Site;
	show: boolean;
}

const emit = defineEmits<Emits>();
const props = defineProps<Props>();

const owner = ref("");
const resident = ref("");
const notes = ref("");
const address = ref(new Address());
const showAddressModal = ref(false);
const saving = ref(false);

const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

function showToast(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

// Every open starts from the loaded site — the same reset-on-show the address
// picker does — so the fields show the stored record, never an abandoned edit.
function resetState() {
	owner.value = props.site.owner?.name ?? "";
	resident.value = props.site.resident?.name ?? "";
	notes.value = props.site.notes ?? "";
	address.value = props.site.address;
	showAddressModal.value = false;
}

watch(
	() => props.show,
	(newVal) => {
		if (newVal) {
			resetState();
		}
	},
);

function handleCancel() {
	emit("close");
}

function onAddressSaved(saved: Address) {
	address.value = saved;
	showAddressModal.value = false;
}

async function handleSave() {
	if (!address.value.gid || saving.value) {
		return;
	}
	saving.value = true;
	try {
		await apiClient.JSONPut(props.site.uri, {
			address: { gid: address.value.gid },
			owner: owner.value,
			resident: resident.value,
			notes: notes.value,
		} satisfies SiteUpdateRequest);
		emit("saved");
	} catch (e) {
		// The dialog stays open: the visitor's edits are still there to retry.
		log.error("Failed to save site", e);
		showToast(
			"Save failed",
			e instanceof Error ? e.message : "Failed to save site",
		);
	} finally {
		saving.value = false;
	}
}
</script>
